// src/lib/persistentCache.js
//
// Stage 2 — IndexedDB persistence for entityCache. Makes email bodies, threads,
// and entity records (deals / projects / contacts / etc) survive page reloads.
// Works as a silent layer beneath entityCache: callers still import from
// entityCache unchanged; persistentCache hydrates on app boot and writes
// through on every set.
//
// Size budget: ~20–50MB for most users (Chrome desktop allows hundreds of MB
// per origin; iOS Safari caps ~50MB). Entries older than PERSIST_TTL_DAYS are
// pruned on hydrate so we don't grow unbounded.

import Dexie from 'dexie'
import { _registerPersistentWriter, set as setCache } from './entityCache'

const DB_NAME = 'ChatroneOfflineCache'
const PERSIST_TTL_DAYS = 30

// Compound primary key [type+key] lets us upsert cleanly without a secondary
// lookup. type index supports bulk-delete-by-type; updatedAt index supports
// TTL pruning.
const db = new Dexie(DB_NAME)
db.version(1).stores({
  entries: '[type+key], type, updatedAt',
})

let _hydrated = false
let _pendingWrites = new Map()  // key `${type}:${key}` → { type, key, value }
let _flushTimer = null
const WRITE_DEBOUNCE_MS = 500   // batch bursts of sets into one IDB txn

function schedulePersist(type, key, value) {
  _pendingWrites.set(`${type}:${key}`, { type, key, value })
  if (_flushTimer) return
  _flushTimer = setTimeout(flushPending, WRITE_DEBOUNCE_MS)
}

async function flushPending() {
  const batch = [..._pendingWrites.values()]
  _pendingWrites.clear()
  _flushTimer = null
  if (batch.length === 0) return
  try {
    const now = Date.now()
    await db.entries.bulkPut(batch.map(e => ({
      type: e.type,
      key: e.key,
      value: e.value,
      updatedAt: now,
    })))
  } catch (err) {
    // Non-fatal — in-memory cache still works; worst case we lose persistence
    // for this batch. Common cause: storage quota exceeded. Log and continue.
    console.warn('[persistentCache] bulkPut failed:', err?.message)
  }
}

/**
 * Hydrate the in-memory entityCache from IndexedDB, then register the
 * write-through hook so future sets persist. Call once at app boot, before
 * render if possible — but if called after, hooks will cache-miss on first
 * render, fetch from the server, and re-populate on the way.
 *
 * `skip(type, key)` — when provided, a row matching it is NOT hydrated into
 * the in-memory cache THIS boot. The one caller (src/main.jsx, driven by
 * src/lib/demoCacheReset.js) needs this for the "failed IndexedDB clear"
 * case: a clear that could not prove it removed a known-stale key must not
 * have that same stale value read straight back out of disk a moment later
 * by this very hydration — omitting the version-stamp write (so the NEXT
 * boot retries the clear) is not enough, because nothing stops THIS boot
 * from rendering the stale row in the meantime.
 *
 * Returns { hydrated: N, pruned: M } so callers can log / debug.
 */
export async function initPersistentCache({ skip } = {}) {
  if (_hydrated) return { hydrated: 0, pruned: 0, alreadyInit: true }
  _hydrated = true

  const cutoff = Date.now() - PERSIST_TTL_DAYS * 86400 * 1000
  let hydratedCount = 0
  let prunedCount = 0
  let skippedCount = 0

  try {
    // Load everything newer than cutoff and seed memory cache.
    // Sort ascending by updatedAt so the most recently used entries are
    // seeded LAST — they therefore sit at the "hot" end of each LRU store
    // and survive if the store is near capacity. Without this sort, entries
    // are returned in primary-key order ([type+key] lex), which means
    // "contacts:*" loads before "inbox:*" and gets evicted by the inbox
    // polling entries that follow.
    const rows = await db.entries.where('updatedAt').above(cutoff).sortBy('updatedAt')
    for (const row of rows) {
      if (skip && skip(row.type, row.key)) { skippedCount++; continue }
      // Direct setCache call bypasses the persistent writer (not yet
      // registered) so we don't re-write what we just read.
      setCache(row.type, row.key, row.value)
      hydratedCount++
    }
    if (skippedCount > 0) console.info(`[persistentCache] skipped ${skippedCount} entries excluded by caller`)

    // Background: prune older-than-TTL rows without blocking boot.
    setTimeout(async () => {
      try {
        prunedCount = await db.entries.where('updatedAt').belowOrEqual(cutoff).delete()
        if (prunedCount > 0) console.info(`[persistentCache] pruned ${prunedCount} stale entries`)
      } catch (err) {
        console.warn('[persistentCache] prune failed:', err?.message)
      }
    }, 5000)
  } catch (err) {
    console.warn('[persistentCache] hydrate failed (will continue without persistence):', err?.message)
    _hydrated = false  // allow retry on next call
    return { hydrated: 0, pruned: 0, error: err?.message }
  }

  // Register write-through AFTER hydration so the initial seeding doesn't
  // write back to disk (infinite loop / wasted IO).
  _registerPersistentWriter(schedulePersist)

  // Tell mounted hooks the warm cache is now available. After a page reload the
  // in-memory cache is empty and this hydration runs async — the exact window
  // where the network is most likely still wedged from sleep. Hooks that mounted
  // cold (loading=true) listen for this and paint their cached data immediately
  // instead of sitting on skeletons until a slow/hung first fetch resolves.
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent('chatrone:cache-hydrated', { detail: { hydrated: hydratedCount } }))
    } catch { /* CustomEvent unavailable — non-fatal */ }
  }

  return { hydrated: hydratedCount, pruned: prunedCount }
}

// Manual purge — useful for logout / account switch / debugging.
export async function clearPersistentCache() {
  try {
    await db.entries.clear()
    _pendingWrites.clear()
    if (_flushTimer) { clearTimeout(_flushTimer); _flushTimer = null }
    return true
  } catch (err) {
    console.warn('[persistentCache] clear failed:', err?.message)
    return false
  }
}

// Bounded purge — every IndexedDB row of one TYPE bucket (the `type` index on
// `entries` makes this a single indexed query, not a table scan). Narrower
// than clearPersistentCache(), which wipes everything (bodies, threads, every
// list/entity read) regardless of whether the caller has any reason to
// distrust those buckets. First caller: src/lib/demoCacheReset.js, invalidating
// only the business-list/entity buckets on a demo scenario-content version
// bump — email bodies/threads are untouched because a reseed never writes
// those. Any write already queued for THIS type is dropped too, so a flush
// racing the clear can't silently resurrect what was just deleted.
export async function clearPersistentType(type) {
  try {
    await db.entries.where('type').equals(type).delete()
    for (const key of [..._pendingWrites.keys()]) {
      if (key.startsWith(`${type}:`)) _pendingWrites.delete(key)
    }
    return true
  } catch (err) {
    console.warn(`[persistentCache] clear type "${type}" failed:`, err?.message)
    return false
  }
}

// Bounded purge, by KEY rather than whole type — the narrower sibling to
// clearPersistentType(), for a caller (src/lib/demoCacheReset.js) that must
// only drop the specific business-list/entity keys a scenario retirement/
// reseed actually touches, never the whole 'list'/'entity' bucket (an
// unrelated module's cached contacts/companies/inbox reads have no reason to
// be invalidated by a demo-content version bump). Reads every row of the
// type (the indexed query clearPersistentType already uses), filters in JS
// by `predicate(key)`, then bulkDeletes exactly those compound primary keys
// ([type, key] — the schema's own `[type+key]` compound index order).
// Pending writes matching the SAME predicate are dropped too, so a flush
// racing the clear can't resurrect a row this call just removed.
export async function clearPersistentMatching(type, predicate) {
  try {
    const rows = await db.entries.where('type').equals(type).toArray()
    const toDelete = rows.filter((r) => predicate(r.key)).map((r) => [r.type, r.key])
    if (toDelete.length) await db.entries.bulkDelete(toDelete)
    for (const [k, v] of [..._pendingWrites.entries()]) {
      if (v.type === type && predicate(v.key)) _pendingWrites.delete(k)
    }
    return true
  } catch (err) {
    console.warn(`[persistentCache] clear matching "${type}" failed:`, err?.message)
    return false
  }
}

// Diagnostic — expose for console debugging during dev.
export async function persistentStats() {
  try {
    const total = await db.entries.count()
    const byType = {}
    await db.entries.each(row => { byType[row.type] = (byType[row.type] || 0) + 1 })
    return { total, byType, pendingWrites: _pendingWrites.size }
  } catch (err) {
    return { error: err?.message }
  }
}

if (typeof window !== 'undefined') {
  window.__persistentStats = persistentStats
  window.__clearPersistent = clearPersistentCache
}
